
import { type FilterOptions } from '@resolute/schema';
import { Container, Section, Sheet } from '@resolute/ui';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';


import { useFilterOptions, usePropertyMapResults, usePropertySearch } from './api';
import { ActiveFilters } from './components/active-filters';
import { FilterPanel } from './components/filter-panel';
import { Pagination } from './components/pagination';
import { ResultsGrid } from './components/results-grid';
import { ResultsToolbar, type ResultsView } from './components/results-toolbar';
import { countActiveFilters, DEFAULT_SEARCH_STATE, type SearchState } from './search-query';
import { useSearchState } from './use-search-state';

import { clientEnv } from '@/lib/env';
import { Seo } from '@/lib/seo';

// MapLibre is heavy — load the map only when the user opens the map view.
const ResultsMap = lazy(() => import('./components/results-map'));

export interface SearchPageProps {
  /** Preset filters applied once on mount (for /rent, /commercial landing routes). */
  preset?: Partial<SearchState>;
  presetKey?: keyof SearchState;
  heading?: string;
  /** Canonical base path for SEO (e.g. /inventory); defaults to /search. */
  canonicalPath?: string;
}

/** `state.city` is a CPzone city id (see filter-options), not a name — so the
 *  page heading resolves it against the fetched options, not a static map. */
function deriveHeading(state: SearchState, fallback: string, cities?: FilterOptions['cities']): string {
  const cityName = state.city ? (cities?.find((c) => c.value === state.city)?.label ?? state.city) : null;
  const base = state.transaction === 'rent' ? 'Inventory for rent' : fallback;
  return cityName ? `${base} in ${cityName}` : base;
}

export default function SearchPage({
  preset,
  presetKey,
  heading = 'Inventory for sale',
  canonicalPath = '/search',
}: SearchPageProps) {
  const { state, setFilters, toggleArrayValue, setSort, setPage, clear } = useSearchState();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [view, setView] = useState<ResultsView>('list');
  const appliedPreset = useRef(false);

  // Apply the route preset once, only if not already present in the URL.
  // Compared against the field's own default (not `== null`) so this works
  // for boolean preset fields too — `preLeased`/`giftCity` default to
  // `false`, not `null`/`undefined`, so an `== null` check never fires for
  // them and /invest, /gift-city silently never applied their filter.
  useEffect(() => {
    if (appliedPreset.current || !preset || !presetKey) return;
    appliedPreset.current = true;
    if (state[presetKey] === DEFAULT_SEARCH_STATE[presetKey]) setFilters(preset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const query = usePropertySearch(state);
  const mapQuery = usePropertyMapResults(state, view === 'map');
  const filterOptionsQuery = useFilterOptions();
  // The route's own preset filter (if any) is this page's canonical identity,
  // not a user-applied filter — exclude it before counting, so e.g. /rent
  // isn't noindexed just for having `transaction=rent` set. Any OTHER filter
  // the user adds on top still correctly triggers noindex.
  const activeCount = countActiveFilters(
    presetKey ? { ...state, [presetKey]: DEFAULT_SEARCH_STATE[presetKey] } : state,
  );
  const title = deriveHeading(state, heading, filterOptionsQuery.data?.cities);

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: clientEnv.siteUrl },
      { '@type': 'ListItem', position: 2, name: title },
    ],
  };

  return (
    <>
      <Seo
        title={title}
        description={`Browse ${title.toLowerCase()} — verified, RERA-transparent listings with transparent pricing and investment insights on Resolute Property Solutions.`}
        path={canonicalPath}
        // Filtered result pages are not indexed (canonical points at the base).
        noindex={activeCount > 0 || state.page > 1}
        jsonLd={breadcrumbJsonLd}
      />

      <Section spacing="md">
        <Container>
          <div className="mb-6">
            <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h1>
          </div>

          <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
            {/* Desktop filter rail */}
            <aside className="hidden lg:block">
              <div className="sticky top-[calc(var(--header-offset)+1rem)] max-h-[calc(100vh-var(--header-offset)-2rem)] overflow-y-auto rounded-lg border border-border bg-card p-5">
                <FilterPanel
                  state={state}
                  facets={query.data?.meta.facets}
                  options={filterOptionsQuery.data}
                  optionsLoading={filterOptionsQuery.isLoading}
                  optionsError={filterOptionsQuery.isError}
                  onRetryOptions={() => filterOptionsQuery.refetch()}
                  setFilters={setFilters}
                  toggleArrayValue={toggleArrayValue}
                  clear={clear}
                />
              </div>
            </aside>

            <div className="min-w-0">
              <div className="flex flex-col gap-4">
                <ResultsToolbar
                  state={state}
                  total={query.data?.meta.total}
                  isLoading={query.isLoading}
                  activeFilterCount={activeCount}
                  setSort={setSort}
                  onOpenFilters={() => setFiltersOpen(true)}
                  view={view}
                  onViewChange={setView}
                />
                <ActiveFilters
                  state={state}
                  options={filterOptionsQuery.data}
                  setFilters={setFilters}
                  toggleArrayValue={toggleArrayValue}
                  clear={clear}
                />
              </div>

              <div className="mt-6">
                {view === 'map' ? (
                  <Suspense
                    fallback={
                      <div className="h-[70vh] min-h-[440px] w-full animate-pulse rounded-xl border border-border bg-muted" />
                    }
                  >
                    <ResultsMap properties={mapQuery.data?.data} isLoading={mapQuery.isLoading} />
                  </Suspense>
                ) : (
                  <ResultsGrid
                    items={query.data?.data}
                    isLoading={query.isLoading}
                    isError={query.isError}
                    isFetching={query.isFetching}
                    onRetry={() => query.refetch()}
                    onClearFilters={clear}
                  />
                )}
              </div>

              {view === 'list' && query.data && (
                <Pagination
                  page={query.data.meta.page}
                  totalPages={query.data.meta.totalPages}
                  onPageChange={setPage}
                />
              )}
            </div>
          </div>
        </Container>
      </Section>

      {/* Mobile filter sheet */}
      <Sheet
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        title="Filters"
        side="bottom"
        footer={
          <button
            type="button"
            onClick={() => setFiltersOpen(false)}
            className="w-full rounded bg-primary py-2.5 text-sm font-medium text-primary-foreground"
          >
            Show {query.data?.meta.total ?? ''} results
          </button>
        }
      >
        <FilterPanel
          state={state}
          facets={query.data?.meta.facets}
          options={filterOptionsQuery.data}
          optionsLoading={filterOptionsQuery.isLoading}
          optionsError={filterOptionsQuery.isError}
          onRetryOptions={() => filterOptionsQuery.refetch()}
          setFilters={setFilters}
          toggleArrayValue={toggleArrayValue}
          clear={clear}
        />
      </Sheet>
    </>
  );
}
